import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SectionTag } from "@/components/Motion";
import PageSeo from "@/components/PageSeo";
import { TRADES, tradeLabel } from "@/data/content";
import { TARIFS, formatRange, formatMonthlyRange } from "@/data/tarifs";
import { COMPANY } from "@/data/company";

const LIST_LD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Assurance décennale par métier",
  itemListElement: TRADES.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `Assurance décennale ${tradeLabel(t)}`,
    url: `${COMPANY.url}/metiers/${t.slug}`,
  })),
};

export default function Metiers() {
  return (
    <div data-testid="metiers-page" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-24">
      <PageSeo path="/metiers" jsonLd={LIST_LD} />
      <SectionTag className="mb-4">Couverture RC Pro + Décennale</SectionTag>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.9] mb-6">
        Assurance décennale<br />par <span className="text-accent">métier.</span>
      </h1>
      <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-ink/80 mb-16">
        Retrouvez les prix indicatifs et les garanties à examiner pour 22 métiers du bâtiment.
        Chaque fiche explique comment chercher une décennale moins chère et choisir un contrat
        adapté à vos travaux, avec les points à vérifier avant de demander un devis.
      </p>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-ink/15">
        {TRADES.map((t, i) => (
          <li key={t.slug} className="border-b border-r border-ink/15">
            <Link
              to={`/metiers/${t.slug}`}
              data-testid={`metier-card-${i}`}
              className="group flex h-full flex-col p-7 min-h-[200px] transition-colors hover:bg-ink hover:text-paper"
            >
              <span className="flex items-start justify-between">
                <span className="font-mono text-[11px] tracking-[0.25em] text-ink/60 group-hover:text-paper/60">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <ArrowUpRight className="h-5 w-5 text-ink/60 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent" aria-hidden="true" />
              </span>
              <div className="mt-auto pt-8">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-2">{t.name}</h2>
                <p className="text-sm leading-relaxed text-ink/65 group-hover:text-paper/75">{t.desc}</p>
                <p className="mt-4 font-mono text-sm font-medium text-accent group-hover:text-paper">
                  {formatRange(TARIFS[t.slug])} / an · ≈ {formatMonthlyRange(TARIFS[t.slug])} / mois
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs leading-relaxed text-ink/60 max-w-3xl">
        Fourchettes indicatives de prime annuelle RC Pro + décennale communiquées par le cabinet. La prime réelle dépend de l’étude de votre dossier.
      </p>

      <div className="mt-16 border-2 border-ink bg-white p-8 sm:p-12 shadow-brutal flex flex-wrap items-center justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter">
            Votre métier n'apparaît pas ?
          </h2>
          <p className="mt-2 text-sm text-ink/75 max-w-xl">
            Décrivez votre activité dans le formulaire : nous étudions les profils du BTP,
            y compris les sociétés en création.
          </p>
        </div>
        <Link
          to="/devis"
          data-testid="metiers-cta-devis"
          className="inline-flex items-center gap-2 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-ink"
        >
          Demander un devis
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
